import type { PostHog, Properties } from "posthog-js/dist/module.slim";
import { parseCityState } from "./format";
import { whenIdle } from "./whenIdle";

/*
 * PostHog on the marketing site, modelled on the booking app's lib/posthog.ts (PandahubClientNextJs). Same project
 * token and the default persistence, whose cookie is set on .pandahub.com: a visitor is one person from their first
 * page here to their booking on app.pandahub.com. Loaded by components/common/Analytics.tsx once the page is idle.
 *
 * Every visitor is tracked, whatever they choose in the CookieYes banner (owner, Oct 30: mandatory tracking); the
 * banner is notice-only. Nothing here reads CookieYes or opts anyone out.
 */

let client: Promise<PostHog | null> | undefined;

const registered: Properties = { app: "marketing_site", platform: "web" };

// The page's tags go on at send time, from the URL the event is sent on, not with register: PostHog sends a client
// navigation's $pageview in the same task as the URL change, before React could register the new page's tags.
export function pageTags(path: string): Properties {
  if (path === "/") return { page_type: "home" };
  // a location's slug ends in its two-letter state or province (phoenix-az, toronto-on); car-detailing/classic-cars
  // and the other service pages don't
  const location = /^\/(?:ca\/)?car-detailing\/([a-z0-9-]+-[a-z]{2})\/?$/.exec(path);
  if (location) return { page_type: "location", ...parseCityState(location[1]) };
  const lp = /^\/lp\/([^/]+)/.exec(path);
  if (lp) return { page_type: "landing", lp: lp[1] };
  if (/^\/(?:blog|topic|authors)(?:\/|$)/.test(path)) return { page_type: "blog" };
  if (/^\/packages\/?$/.test(path)) return { page_type: "packages" };
  return { page_type: "other" };
}

const idle = () => new Promise<void>((done) => whenIdle(done));

export function loadPosthog(): Promise<PostHog | null> {
  const projectToken = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  if (!projectToken || !host) return Promise.resolve(null);
  // PostHog's slim core, its add-ons and init are three idle tasks, not the full build's one (118 ms on a 4x slowed
  // phone). Only the core's evaluation, one module, still goes over the 50 ms that counts as blocking (about 65 ms).
  client ??= (async () => {
    const { default: posthog } = await import("posthog-js/dist/module.slim");
    await idle();
    // Every event carries the visitor's time zone, and Chrome's first time zone lookup loads its data: 40 of the 77 ms
    // the first $pageview took on a 4x slowed phone. Here it's on its own, before the add-ons' chunk evaluates.
    Intl.DateTimeFormat().resolvedOptions();
    // posthog-js publishes its add-ons as this one file, which doesn't tree-shake: two of them cost what all of them do
    const { AnalyticsExtensions, ErrorTrackingExtensions, FeatureFlagsExtensions, ToolbarExtensions } = await import(
      "posthog-js/dist/extension-bundles"
    );
    await idle();
    posthog.init(projectToken, {
      // through this site (app/api/hub/[...path]/route.ts forwards to the host), so ad blockers that list PostHog's
      // host don't drop the events
      api_host: "/api/hub",
      ui_host: host,
      // PostHog's paths end in a slash (/e/), which Next answers with a 308 to the bare path: a round trip per send.
      // Sent without it; the route puts it back.
      rewriteRequestPath: (url) => {
        url.pathname = url.pathname.replace(/\/$/, "");
        return url;
      },
      defaults: "2026-01-30",
      // only these add-ons exist here, so nothing else (surveys, heatmaps, dead clicks, web vitals, web experiments,
      // session recording) can start, whatever the project's settings
      __extensionClasses: {
        // a $pageview on each client navigation
        historyAutocapture: AnalyticsExtensions.historyAutocapture,
        ...ErrorTrackingExtensions,
        // the price window's car search experiment (lib/featureFlag.ts). It's in the add-ons' file already loaded, so
        // it adds no script; its cost is the one /flags request init sends (through /api/hub, after the idle tasks)
        ...FeatureFlagsExtensions,
        // the toolbar, for staff: it starts only when launched from PostHog (Toolbar, or an action's "Launch" on an
        // authorized URL), which loads its script from ui_host. Visitors never get it; it's in the same add-ons file.
        ...ToolbarExtensions,
      },
      // no click autocapture: it added about 20 ms to a phone's first tap on See my price (owner, Oct 30: no slowdown);
      // the price window's steps, calls, booking-app links and the chat are sent as their own events
      autocapture: false,
      capture_exceptions: true,
      // flags once per page: the variant is read as the price window opens, so a refresh every five minutes would be
      // requests for nothing
      remote_config_refresh_interval_ms: 0,
      // the booking app's: our self-hosted PostHog serves its add-on scripts at /static/<name>.js only
      strict_script_versioning: false,
      before_send: (event) => {
        if (event) event.properties = { ...event.properties, ...pageTags(window.location.pathname) };
        return event;
      },
    });
    posthog.register(registered);
    // init sends the page's first $pageview from a setTimeout(…, 1), but whatever was waiting on this client (a See my
    // price opened before PostHog loaded) would go out first and come before its own page view: ordered funnels then
    // lose that visitor (87 people in a week). Timers of the same delay run in the order set, so one set here runs
    // after init's, once the $pageview is captured.
    await new Promise((done) => setTimeout(done, 1));
    return posthog;
  })();
  return client;
}

export const withPosthog = (use: (posthog: PostHog) => void) => void loadPosthog().then((posthog) => posthog && use(posthog));

export const capture = (event: string, properties?: Properties) => withPosthog((posthog) => posthog.capture(event, properties));
